import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Action } from 'vscode/vscode/vs/base/common/actions';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { getIconClasses } from 'vscode/vscode/vs/editor/common/services/getIconClasses';
import { IModelService } from 'vscode/vscode/vs/editor/common/services/model';
import { ILanguageService } from 'vscode/vscode/vs/editor/common/languages/language';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { IQuickInputService } from 'vscode/vscode/vs/platform/quickinput/common/quickInput.service';
import { IPreferencesService } from 'vscode/vscode/vs/workbench/services/preferences/common/preferences.service';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/platform/configuration/common/configurationRegistry';
import { EditorExtensionsRegistry } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { MenuRegistry, MenuId, isIMenuItem } from 'vscode/vscode/vs/platform/actions/common/actions';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { isLocalizedString } from 'vscode/vscode/vs/platform/action/common/action';

const _moduleId = "vs/workbench/contrib/preferences/browser/preferencesActions";
let ConfigureLanguageBasedSettingsAction = class ConfigureLanguageBasedSettingsAction extends Action {
    static { this.ID = 'workbench.action.configureLanguageBasedSettings'; }
    static { this.LABEL = ( localize2WithPath(_moduleId, 0, "Configure Language Specific Settings...")); }
    constructor(id, label, modelService, languageService, quickInputService, preferencesService) {
        super(id, label);
        this.modelService = modelService;
        this.languageService = languageService;
        this.quickInputService = quickInputService;
        this.preferencesService = preferencesService;
    }
    async run() {
        const languages = this.languageService.getSortedRegisteredLanguageNames();
        const picks = ( (languages.map(({ languageName, languageId }) => {
            const description = ( localizeWithPath(_moduleId, 1, "({0})", languageId));
            let fakeResource;
            const extensions = this.languageService.getExtensions(languageId);
            if (extensions.length) {
                fakeResource = URI.file(extensions[0]);
            }
            else {
                const filenames = this.languageService.getFilenames(languageId);
                if (filenames.length) {
                    fakeResource = URI.file(filenames[0]);
                }
            }
            return {
                label: languageName,
                iconClasses: getIconClasses(this.modelService, this.languageService, fakeResource),
                description
            };
        })));
        await this.quickInputService.pick(picks, { placeHolder: ( localizeWithPath(_moduleId, 2, "Select Language")) })
            .then(pick => {
            if (pick) {
                const languageId = this.languageService.getLanguageIdByLanguageName(pick.label);
                if (typeof languageId === 'string') {
                    return this.preferencesService.openLanguageSpecificSettings(languageId);
                }
            }
            return undefined;
        });
    }
};
ConfigureLanguageBasedSettingsAction = ( (__decorate([
    ( (__param(2, IModelService))),
    ( (__param(3, ILanguageService))),
    ( (__param(4, IQuickInputService))),
    ( (__param(5, IPreferencesService)))
], ConfigureLanguageBasedSettingsAction)));
CommandsRegistry.registerCommand({
    id: '_getAllSettings',
    handler: () => {
        const configRegistry = ( (Registry.as(Extensions.Configuration)));
        const allSettings = configRegistry.getConfigurationProperties();
        return allSettings;
    }
});
CommandsRegistry.registerCommand('_getAllCommands', function (accessor) {
    const keybindingService = accessor.get(IKeybindingService);
    const actions = [];
    for (const editorAction of EditorExtensionsRegistry.getEditorActions()) {
        const keybinding = keybindingService.lookupKeybinding(editorAction.id);
        actions.push({
            command: editorAction.id,
            label: editorAction.label,
            description: isLocalizedString(editorAction.metadata?.description) ? editorAction.metadata.description.value : editorAction.metadata?.description,
            precondition: editorAction.precondition?.serialize(),
            keybinding: keybinding?.getLabel() ?? 'Not set'
        });
    }
    for (const menuItem of MenuRegistry.getMenuItems(MenuId.CommandPalette)) {
        if (isIMenuItem(menuItem)) {
            const title = typeof menuItem.command.title === 'string' ? menuItem.command.title : menuItem.command.title.value;
            const category = menuItem.command.category ? typeof menuItem.command.category === 'string' ? menuItem.command.category : menuItem.command.category.value : undefined;
            const label = category ? `${category}: ${title}` : title;
            const description = isLocalizedString(menuItem.command.metadata?.description) ? menuItem.command.metadata.description.value : menuItem.command.metadata?.description;
            const keybinding = keybindingService.lookupKeybinding(menuItem.command.id);
            actions.push({
                command: menuItem.command.id,
                label,
                description,
                precondition: menuItem.when?.serialize(),
                keybinding: keybinding?.getLabel() ?? 'Not set'
            });
        }
    }
    return actions;
});

export { ConfigureLanguageBasedSettingsAction };
